import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFarms, useLots, bulkInitialLocal } from "@/lib/repo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ScanCTA } from "@/components/ScanCTA";
import { CATEGORIES_BOVINO, CATEGORIES_BUFALINO } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { ArrowLeft, Trash2, Plus, CheckCircle2, ClipboardList, Boxes, ArrowRight, Clipboard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { parseChipTokens, readClipboardText } from "@/lib/chipParse";

const STEPS = [
  { n: 1, label: "Configuración" },
  { n: 2, label: "Carga en corral" },
  { n: 3, label: "Resumen" },
];

const SKIP_REASONS = {
  exists: "Ya estaba registrado",
  duplicate_in_batch: "Repetido en esta tanda",
  invalid: "Chip inválido",
};

export default function InitialInventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHandler = user?.role === "handler";

  const [step, setStep] = useState(1);
  const allFarms = useFarms();
  const farms = useMemo(
    () => (isHandler ? allFarms.filter((x) => (user?.assigned_farm_ids || []).includes(x.id)) : allFarms),
    [allFarms, isHandler, user]
  );
  // Fix K (2026-09-18): la raza por defecto pasa a "Mestizo" (pedido del cliente):
  // es la mayoría de su rebaño y así no tiene que corregirla en cada carga.
  const [cfg, setCfg] = useState({ farm_id: "", lot_id: "", sex: "female", breed: "Mestizo", category: "" });
  const lots = useLots(cfg.farm_id);
  const [chips, setChips] = useState([]);
  const [manual, setManual] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [pasteSummary, setPasteSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (farms[0] && !cfg.farm_id) setCfg((c) => ({ ...c, farm_id: farms[0].id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farms]);

  const farmName = useMemo(() => farms.find((f) => f.id === cfg.farm_id)?.name || "—", [farms, cfg.farm_id]);
  const lotName = useMemo(() => lots.find((l) => l.id === cfg.lot_id)?.name || "Sin lote", [lots, cfg.lot_id]);

  const addChip = (raw) => {
    const c = String(raw || "").replace(/\D/g, "");
    if (c.length < 6) { toast.warning("Chip muy corto (mínimo 6 dígitos)"); return; }
    setChips((prev) => {
      if (prev.includes(c)) { toast.info(`El chip ${c} ya está en la lista`); return prev; }
      if ("vibrate" in navigator) navigator.vibrate?.(35);
      return [c, ...prev];
    });
  };

  const removeChip = (c) => setChips((prev) => prev.filter((x) => x !== c));

  const loadPastedList = (text) => {
    const raw = text ?? pasteText;
    if (!raw.trim()) { toast.error("Pegá o escribí una lista de chips"); return; }
    const parsed = parseChipTokens(raw);
    const existingSet = new Set(chips);
    const newOnes = [];
    let skippedExisting = 0;
    parsed.valid.forEach((c) => {
      if (existingSet.has(c)) { skippedExisting++; return; }
      newOnes.push(c);
    });
    if (newOnes.length) {
      setChips((prev) => [...newOnes.reverse(), ...prev]);
      if ("vibrate" in navigator) navigator.vibrate?.(35);
    }
    const totalDup = parsed.duplicates + skippedExisting;
    const parts = [`${newOnes.length} chip${newOnes.length === 1 ? "" : "s"} reconocido${newOnes.length === 1 ? "" : "s"}`];
    if (totalDup) parts.push(`${totalDup} repetido${totalDup === 1 ? "" : "s"} (se ignoran)`);
    if (parsed.invalid.length) parts.push(`${parsed.invalid.length} no válido${parsed.invalid.length === 1 ? "" : "s"}`);
    setPasteSummary(parts.join(" · ") + (parsed.invalid.length ? `: ${parsed.invalid.slice(0, 5).join(", ")}${parsed.invalid.length > 5 ? "…" : ""}` : ""));
    setPasteText("");
    toast.success(`${newOnes.length} chip(s) agregados a la lista`);
  };

  const pasteFromClipboard = async () => {
    const text = await readClipboardText();
    if (text === null) { toast.error("No pude leer el portapapeles — pegá manualmente en el cuadro de texto"); return; }
    if (!text.trim()) { toast.info("El portapapeles está vacío"); return; }
    loadPastedList(text);
  };

  const goToScan = () => {
    if (!cfg.farm_id) return toast.error("Elegí una finca");
    setStep(2);
  };

  const save = async () => {
    if (chips.length === 0) return toast.error("Agregá al menos un chip");
    setSaving(true);
    try {
      const res = await bulkInitialLocal({
        farm_id: cfg.farm_id,
        lot_id: cfg.lot_id || null,
        sex: cfg.sex,
        breed: cfg.breed || null,
        category: cfg.category || null,
        chips,
      });
      setResult(res);
      setStep(3);
      toast.success(`${res.total_created} animales cargados${navigator.onLine ? "" : " (se sincronizará al volver la señal)"}`);
    } catch (e) {
      toast.error(e?.userMessage || "No pude guardar la carga");
    } finally { setSaving(false); }
  };

  const reset = () => { setChips([]); setManual(""); setResult(null); setStep(2); };

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-24">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)} className="h-9 px-2" data-testid="initial-inv-back"><ArrowLeft size={18} /></Button>
        <div className="min-w-0">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Carga masiva del rebaño</div>
          <h1 className="font-editorial text-[30px] sm:text-[36px] leading-[1.05] tracking-tight">Inventario inicial</h1>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2" data-testid="initial-inv-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={cn("flex items-center gap-2 rounded-full px-3 h-8 border text-[12px] font-medium transition-colors",
              step === s.n ? "bg-primary text-primary-foreground border-primary"
              : step > s.n ? "bg-[hsl(var(--state-present-bg))] text-[hsl(var(--state-present))] border-[hsl(var(--state-present)/0.4)]"
              : "bg-card text-muted-foreground border-border")}>
              <span className="font-mono tabular-nums">{step > s.n ? "✓" : s.n}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — Configuración general */}
      {step === 1 && (
        <div className="rounded-[var(--radius)] border border-border bg-card p-5 shadow-paper space-y-5" data-testid="initial-inv-step1">
          <div className="flex items-start gap-3">
            <Boxes size={20} className="text-primary mt-0.5 shrink-0" />
            <p className="text-[13px] text-muted-foreground">
              Definí los datos <strong className="text-foreground">generales</strong> que se aplican a todos los chips de esta tanda.
              Después escaneás en el corral y completás los detalles de cada uno en la oficina.
            </p>
          </div>

          <div>
            <Label>Ubicación (finca)</Label>
            <Select value={cfg.farm_id} onValueChange={(v) => setCfg({ ...cfg, farm_id: v, lot_id: "" })} disabled={isHandler && farms.length <= 1}>
              <SelectTrigger className="h-12" data-testid="initial-inv-farm"><SelectValue placeholder="Elegir finca" /></SelectTrigger>
              <SelectContent>{farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Lote (opcional)</Label>
            <Select value={cfg.lot_id || "none"} onValueChange={(v) => setCfg({ ...cfg, lot_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-12" data-testid="initial-inv-lot"><SelectValue placeholder="Sin lote" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin lote</SelectItem>
                {lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sexo general</Label>
              <Select value={cfg.sex} onValueChange={(v) => setCfg({ ...cfg, sex: v })}>
                <SelectTrigger className="h-12" data-testid="initial-inv-sex"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Hembra</SelectItem>
                  <SelectItem value="male">Macho</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raza general</Label>
              <Input className="h-12" value={cfg.breed} onChange={(e) => setCfg({ ...cfg, breed: e.target.value })} data-testid="initial-inv-breed" placeholder="Mestizo" />
            </div>
          </div>

          <div>
            <Label>Categoría general (opcional)</Label>
            <Select value={cfg.category || "none"} onValueChange={(v) => setCfg({ ...cfg, category: v === "none" ? "" : v })}>
              <SelectTrigger className="h-12" data-testid="initial-inv-category"><SelectValue placeholder="Sin categoría" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin categoría (la completo después)</SelectItem>
                <SelectGroup>
                  <SelectLabel>Bovino</SelectLabel>
                  {CATEGORIES_BOVINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Bufalino</SelectLabel>
                  {CATEGORIES_BUFALINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={goToScan} className="h-12 w-full" data-testid="initial-inv-continue">
            Continuar a la carga <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      )}

      {/* STEP 2 — Carga masiva en el corral */}
      {step === 2 && (
        <div className="space-y-4" data-testid="initial-inv-step2">
          {/* Config summary */}
          <div className="rounded-[var(--radius)] border border-border bg-muted/40 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]">
            <span className="font-mono uppercase tracking-[0.1em] text-[10.5px] text-muted-foreground">General</span>
            <span><strong>{farmName}</strong></span>
            <span className="text-muted-foreground">·</span>
            <span>{lotName}</span>
            <span className="text-muted-foreground">·</span>
            <span>{cfg.sex === "female" ? "Hembra" : "Macho"}</span>
            {cfg.breed && <><span className="text-muted-foreground">·</span><span>{cfg.breed}</span></>}
            <button onClick={() => setStep(1)} className="ml-auto text-primary font-medium hover:underline underline-offset-4" data-testid="initial-inv-edit-cfg">Editar</button>
          </div>

          {/* Counter */}
          <div className="rounded-[var(--radius)] border border-border bg-card p-5 shadow-paper">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Chips leídos</div>
            <div className="mt-1 font-editorial tabular-nums text-[64px] leading-none tracking-tight text-primary" data-testid="initial-inv-count">{chips.length}</div>
          </div>

          {/* Scan + manual */}
          <div className="space-y-2.5">
            <ScanCTA testId="initial-inv-scan" onChip={addChip} label="Escanear chip" />
            <div className="flex gap-2">
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter" && manual) { addChip(manual); setManual(""); } }}
                placeholder="Ingresar chip a mano…"
                className="h-12 font-mono tabular-nums tracking-[0.04em]"
                data-testid="initial-inv-manual-input"
              />
              <Button variant="outline" className="h-12 px-4" onClick={() => { if (manual) { addChip(manual); setManual(""); } }} data-testid="initial-inv-manual-add">
                <Plus size={16} />
              </Button>
            </div>
          </div>

          {/* Pegado masivo — Notas del iPhone, WhatsApp o Excel */}
          <div className="rounded-[var(--radius)] border border-border bg-card p-4 shadow-paper space-y-2.5" data-testid="initial-inv-paste-section">
            <div className="flex items-center gap-2 text-[12.5px] font-heading font-semibold text-foreground">
              <ClipboardList size={15} className="text-primary" /> Pegar lista de chips
            </div>
            <Textarea
              value={pasteText}
              onChange={(e) => { setPasteText(e.target.value); setPasteSummary(null); }}
              rows={4}
              placeholder="Pegá la lista de chips — uno por línea, separados por coma, etc…"
              className="font-mono text-[13px]"
              data-testid="initial-inv-paste-textarea"
            />
            <div className="grid grid-cols-2 gap-2.5">
              <Button type="button" variant="outline" className="h-11" onClick={pasteFromClipboard} data-testid="initial-inv-paste-clipboard">
                <Clipboard size={15} /> Pegar del portapapeles
              </Button>
              <Button type="button" variant="outline" className="h-11" onClick={() => loadPastedList()} data-testid="initial-inv-paste-load">
                <Plus size={15} /> Cargar lista
              </Button>
            </div>
            {pasteSummary && (
              <p className="text-[12.5px] text-muted-foreground flex items-start gap-1.5 leading-relaxed" data-testid="initial-inv-paste-summary">
                <AlertTriangle size={13} className="shrink-0 mt-0.5 text-[hsl(var(--state-unknown))]" />{pasteSummary}
              </p>
            )}
          </div>

          {/* Chips list */}
          <div className="rounded-[var(--radius)] border border-border bg-card shadow-paper overflow-hidden" data-testid="initial-inv-list">
            {chips.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Todavía no agregaste chips. Escaneá o cargá uno a mano.</div>
            ) : (
              <div className="divide-y divide-border max-h-[36vh] overflow-y-auto">
                {chips.map((c) => (
                  <div key={c} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <span className="font-mono text-[13.5px] tabular-nums tracking-[0.04em]">{c}</span>
                    <button onClick={() => removeChip(c)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`initial-inv-remove-${c}`} aria-label="Quitar">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={save} disabled={saving || chips.length === 0} className="h-12 w-full" data-testid="initial-inv-save">
            {saving ? "Guardando…" : `Guardar ${chips.length} ${chips.length === 1 ? "animal" : "animales"}`}
          </Button>
        </div>
      )}

      {/* STEP 3 — Resumen */}
      {step === 3 && result && (
        <div className="space-y-4" data-testid="initial-inv-step3">
          <div className="rounded-[var(--radius)] border border-[hsl(var(--state-present)/0.4)] bg-[hsl(var(--state-present-bg))] p-6 text-center shadow-paper">
            <CheckCircle2 size={36} className="text-[hsl(var(--state-present))] mx-auto mb-2" />
            <div className="font-editorial text-[40px] leading-none tabular-nums text-foreground" data-testid="initial-inv-result-created">{result.total_created}</div>
            <p className="text-sm text-foreground/80 mt-1">animales cargados en <strong>{farmName}</strong></p>
            {result.total_skipped > 0 && (
              <p className="text-[12.5px] text-muted-foreground mt-1" data-testid="initial-inv-result-skipped">{result.total_skipped} chip(s) omitido(s)</p>
            )}
          </div>

          {result.skipped?.length > 0 && (
            <div className="rounded-[var(--radius)] border border-border bg-card shadow-paper overflow-hidden">
              <div className="px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.12em] text-muted-foreground border-b border-border">Chips omitidos</div>
              <div className="divide-y divide-border max-h-[28vh] overflow-y-auto">
                {result.skipped.map((s, i) => (
                  <div key={s.chip + i} className="px-4 py-2.5 flex items-center justify-between gap-3 text-[13px]">
                    <span className="font-mono tabular-nums">{s.chip}</span>
                    <span className="text-muted-foreground text-[12px]">{SKIP_REASONS[s.reason] || s.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => navigate("/animales?pendientes=1")} className="h-12 flex-1" data-testid="initial-inv-complete-details">
              <ClipboardList size={16} className="mr-1.5" /> Completar detalles
            </Button>
            <Button variant="outline" onClick={reset} className="h-12" data-testid="initial-inv-another">Cargar otra tanda</Button>
          </div>
        </div>
      )}
    </div>
  );
}
